import React, { Fragment, useState, useRef, useEffect, } from 'react';
import { FormGroup, Label, Input } from 'reactstrap';
import { get, } from 'lodash';
import Select from 'react-select';
import classnames from 'classnames';
import DatePicker from 'react-datepicker';
import { setHours, setMinutes, getHours, getMinutes } from 'date-fns';
import { useToggle } from 'react-use';

import texts from '../shared/texts';
import RichTextEditor from './RichTextEditor';

const validationText = (documentName, fieldName, key) => {
  return (
    get(texts.validations[documentName], `${fieldName}.${key}`)
    || get(texts.validations.general, key)
  );
}

export default function Field (props) {
  const { documentName, type, name: fieldName, label, value, setValue, options = [], validationErrors = [], readOnly = _ => false, shouldHide, placeholder, rows, hint, } = props;
  const [hasStarted, setHasStarted] = useState(false);
  const setValueAndStart = (value) => {
    setValue(value);
    setHasStarted(true);
  };
  const [isHtmlMode, toggleHtmlMode] = useToggle(false); // NOTE: リッチテキスト用。もう少しstate増えるならコンポーネントを分けたいところ。
  const isValid = validationErrors.length === 0;
  const validationCss = hasStarted && classnames({ 'is-valid': isValid, 'is-invalid': isValid === false });
  const richTextEditorRef = useRef(null);
  if(shouldHide) return null;

  return (
    <FormGroup key={fieldName}>
      {
        ({
          string: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Input name={fieldName} value={value || ''} onChange={_ => setValueAndStart(_.target.value)} className={validationCss} readOnly={readOnly(value, props)} placeholder={placeholder} />
            </Fragment>
          ),
          password: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Input type="password" name={fieldName} value={value || ''} onChange={_ => setValueAndStart(_.target.value)} className={validationCss} readOnly={readOnly(value, props)} placeholder={placeholder} />
            </Fragment>
          ),
          integer: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Input name={fieldName} type="number" step="1" value={value != null ? value : ''} onChange={_ => setValueAndStart(parseInt(_.target.value))} className={classnames('text-right', validationCss)} readOnly={readOnly(value, props)} placeholder={placeholder} />
            </Fragment>
          ),
          boolean: () => (
            <Fragment>
              <FormGroup check>
                <Label check>
                  <Input name={fieldName} type="checkbox" checked={value} onChange={_ => setValueAndStart(_.target.checked)} className={validationCss} disabled={readOnly(value, props)} />
                  {label}
                </Label>
              </FormGroup>
            </Fragment>
          ),
          select: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Select
                data-test={fieldName}
                value={options.find(_ => _.value === value) || null}
                onChange={_ => setValueAndStart(_ && _.value)}
                className={classnames('form-select', validationCss)}
                options={options}
                isDisabled={readOnly(value, props)}
                isClearable
              />
            </Fragment>
          ),
          multiSelect: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Select
                data-test={fieldName}
                isMulti
                value={options.filter(_ => (value || []).includes(_.value)) || null}
                onChange={_ => setValueAndStart((_ || []).map(_ => _.value))}
                className={classnames('form-select', validationCss)}
                options={options}
                isDisabled={readOnly(value, props)}
                isClearable
              />
            </Fragment>
          ),
          date: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <div>
                <DatePicker
                  data-test={fieldName}
                  dateFormat="yyyy/MM/dd"
                  timeIntervals={10}
                  selected={value}
                  onChange={setValueAndStart}
                  className={classnames('form-control', validationCss)}
                  readOnly={readOnly(value, props)}
                  placeholder={placeholder}
                />
              </div>
            </Fragment>
          ),
          time: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <div>
                <DatePicker
                  data-test={fieldName}
                  showTimeSelect
                  showTimeSelectOnly
                  dateFormat="HH:mm"
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  selected={value}
                  onChange={_ => value != null ? setValueAndStart(setHours(setMinutes(value, getMinutes(_)), getHours(_))) : _}
                  className={classnames('form-control', validationCss)}
                  readOnly={readOnly(value, props)}
                  placeholder={placeholder}
                />
              </div>
            </Fragment>
          ),
          text: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Input type="textarea" value={value} onChange={_ => setValueAndStart(_.target.value)} className={validationCss} readOnly={readOnly(value, props)} placeholder={placeholder} rows={rows} />
            </Fragment>
          ),
          richText: () => (
            <Fragment>
              <div className="d-flex justify-content-between">
                {label && <Label>{label}</Label>}
                <div className="d-flex gap-1 align-items-center">
                  <FormGroup check>
                    <Label check>
                      <Input type="checkbox" checked={isHtmlMode} onChange={_ => toggleHtmlMode(_.target.checked)} />
                      HTMLモード
                    </Label>
                  </FormGroup>
                </div>
              </div>
              {
                isHtmlMode ? (
                  <Input type="textarea" value={value} onChange={_ => setValueAndStart(_.target.value)} className={validationCss} readOnly={readOnly(value, props)} placeholder={placeholder} rows={rows} />
                ) : (
                  <div className="position-relative" style={{ zIndex: 0}}>
                    <RichTextEditor ref={richTextEditorRef} value={value} onChange={setValueAndStart} className={validationCss} readOnly={readOnly(value, props)} placeholder={placeholder} rows={rows} />
                  </div>
                )
              }
            </Fragment>
          ),
        })[type]()
      }
      {hint && <div style={{ whiteSpace: 'pre-line', }}><small className="form-text text-muted">{hint}</small></div>}
      {
        hasStarted && type !== 'list' && validationErrors.length > 0 && (
          <div className="small text-danger mt-1">
            {
              validationErrors.map(k => (
                <div key={k}>{validationText(documentName, fieldName.replace(/\.\d+\./g, '.fields.'), k)}</div>
              ))
            }
          </div>
        )
      }
    </FormGroup>
  );
};

